// Nine Lives '84 - retro view.
//
// A 320x200 canvas in the CGA palette (black, cyan, magenta, white), drawn
// with plain rectangles and scaled up with nearest-neighbour filtering.
// Later milestones replace the rectangles with proper four-colour sprites.

import { WORLD_H, WORLD_W } from '../sim/constants';
import type { Game } from '../sim/game';
import type { Room } from '../sim/room';
import { BirdcageRoom } from '../sim/rooms/birdcage';
import { BookshelfRoom } from '../sim/rooms/bookshelf';
import { CheeseRoom } from '../sim/rooms/cheese';
import { DogsRoom } from '../sim/rooms/dogs';
import { FeliciaRoom } from '../sim/rooms/felicia';
import { FishbowlRoom } from '../sim/rooms/fishbowl';
import { WATER_SURFACE } from '../sim/constants';
import {
  BIRD,
  CAT,
  CUPID,
  DOG,
  DOG_LYING,
  FISH,
  GIFT,
  HEART,
  HEART_BROKEN,
  MOUSE,
  SPIDER,
  type Bitmap,
} from './sprites';

export const CGA = ['#000000', '#00ffff', '#ff00ff', '#ffffff'];

export class ClassicView {
  readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private frame = 0;

  constructor(container: HTMLElement) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = WORLD_W;
    this.canvas.height = WORLD_H;
    this.canvas.className = 'classic';
    container.appendChild(this.canvas);
    this.ctx = this.canvas.getContext('2d')!;
    this.ctx.imageSmoothingEnabled = false;
  }

  /** Fill a world-space box (bottom-left origin, Y up). */
  private box(x: number, y: number, w: number, h: number, colour: number): void {
    this.ctx.fillStyle = CGA[colour];
    this.ctx.fillRect(Math.round(x), Math.round(WORLD_H - y - h), Math.round(w), Math.round(h));
  }

  /** Outline a world-space box, one pixel wide. */
  private outline(x: number, y: number, w: number, h: number, colour: number): void {
    this.box(x, y, w, 1, colour);
    this.box(x, y + h - 1, w, 1, colour);
    this.box(x, y, 1, h, colour);
    this.box(x + w - 1, y, 1, h, colour);
  }

  /**
   * Draw a bitmap with its bottom-left at world (x, y). `x` pixels take
   * `colour`; digits are fixed palette entries. `flip` mirrors it.
   */
  private sprite(bm: Bitmap, x: number, y: number, colour: number, flip = false): void {
    const h = bm.length;
    const w = bm[0].length;
    const x0 = Math.round(x);
    const top = Math.round(WORLD_H - y - h);
    for (let j = 0; j < h; j++) {
      const row = bm[j];
      for (let i = 0; i < w; i++) {
        const c = row[flip ? w - 1 - i : i];
        if (c === '.') continue;
        this.ctx.fillStyle = CGA[c === 'x' ? colour : Number(c)];
        this.ctx.fillRect(x0 + i, top + j, 1, 1);
      }
    }
  }

  render(g: Game): void {
    this.frame++;
    const ctx = this.ctx;
    ctx.fillStyle = CGA[0];
    ctx.fillRect(0, 0, WORLD_W, WORLD_H);

    const tank = g.room instanceof FishbowlRoom && g.room.phase === 'tank';
    if (tank) this.drawTank(g.room as FishbowlRoom);
    else if (g.room) this.drawRoom(g.room);
    else this.drawAlley(g);

    const c = g.cat;
    const hidden = g.room instanceof CheeseRoom && g.room.crawling > 0;
    if (!hidden && (c.invuln === 0 || this.frame % 8 < 4)) {
      const gasping = tank && (g.room as FishbowlRoom).airFraction < 0.3;
      this.sprite(CAT, c.x, c.y, gasping ? 2 : 3, c.facing < 0);
    }

    ctx.fillStyle = CGA[3];
    ctx.font = '8px monospace';
    ctx.textBaseline = 'top';
    let status = `SCORE ${g.score}   LIVES ${g.lives}   LEVEL ${g.level}`;
    if (g.room) {
      status += `   ${g.room.taskName.toUpperCase()} ${g.room.have}/${g.room.need}   ${g.room.seconds}S`;
    }
    if (tank) {
      const air = (g.room as FishbowlRoom).airFraction;
      this.outline(250, 180, 62, 8, 3);
      this.box(251, 181, Math.round(60 * air), 6, air < 0.3 ? 2 : 1);
    }
    ctx.fillText(status, 4, 2);
    if (g.hint) ctx.fillText(g.hint, 4, 190);
  }

  private drawAlley(g: Game): void {
    const a = g.alley;
    for (const w of a.windows) {
      if (w.open) this.box(w.x + 1, w.y, w.w - 2, w.h - 2, w.felicia ? 2 : 1);
      this.outline(w.x, w.y - 1, w.w, w.h + 1, 3);
      this.box(w.sill.x - 1, w.sill.y, w.sill.w + 2, w.sill.h, 3);
      if (w.felicia) this.sprite(CAT, w.x + 6, w.y + 2, 3, true);
    }
    this.box(0, 0, WORLD_W, 1, 3);
    for (const c of a.cans) this.box(c.x, c.y, c.w, c.h, 2);
    for (let x = 3; x < WORLD_W; x += 6) this.box(x, 0, 3, a.fence.y + a.fence.h - 2, 1);
    for (const l of a.lines) {
      this.box(l.x, l.y, l.w, l.h, 3);
      const offset = ((((g.count * a.drift(l)) / 60) % l.w) + l.w) % l.w;
      for (let k = 0; k < 5; k++) {
        const x = l.x + ((((k * l.w) / 5 + offset) % l.w) + l.w) % l.w;
        this.box(x, l.y - 8, 7, 8, k % 2 ? 1 : 3);
      }
    }

    const d = a.dog;
    if (d.away === 0) this.sprite(DOG, d.x, d.y, 2, d.dir < 0);
    const r = a.rival;
    this.sprite(CAT, r.x, r.y, 1, r.x > 160);
    for (const m of a.mice) this.sprite(MOUSE, m.x, m.y, 3, m.dir < 0);
    for (const j of a.junk) this.box(j.x, j.y, j.w, j.h, 2);
  }

  private drawRoom(r: Room): void {
    this.box(0, 0, WORLD_W, 1, 3);
    if (r instanceof FeliciaRoom) {
      for (const h of r.hearts) {
        this.sprite(h.whole ? HEART : HEART_BROKEN, h.x, h.y + h.h - 14, h.whole ? 2 : 3);
      }
      this.box(r.ledge.x, r.ledge.y, r.ledge.w, r.ledge.h, 3);
      this.sprite(CAT, r.felicia.x, r.felicia.y, 2, true);
      for (const b of r.brothers) this.sprite(CAT, b.x, b.y, b.bribed > 0 ? 3 : 1, b.facing < 0);
      for (const c of r.cupids) this.sprite(CUPID, c.x, c.y, 3, c.side < 0);
      for (const a of r.arrows) {
        this.box(a.x, a.y + 1, a.w - 2, 1, 3);
        this.box(a.x + (a.vx >= 0 ? a.w - 2 : 0), a.y, 2, 3, 2);
      }
      for (const g of r.gifts) this.sprite(GIFT, g.x, g.y, 1);
      return;
    }
    this.outline(r.window.x, r.window.y, r.window.w + 2, r.window.h, 3);
    this.box(r.sill.x, r.sill.y, r.sill.w, r.sill.h, 3);
    this.outline(r.stool.x, r.stool.y, r.stool.w, r.stool.h, 3);
    for (const p of r.prints) this.box(p.x - 2, 1, 4, 2, 2);

    if (r instanceof CheeseRoom) {
      const t = r.table;
      this.box(t.x, t.y, t.w, t.h, 3);
      this.box(t.x + 4, 0, 2, t.y, 3);
      this.box(t.x + t.w - 6, 0, 2, t.y, 3);
      for (const s of r.steps) this.box(s.x, s.y, s.w, s.h, 2);
      for (const h of r.holes) {
        this.box(h.x - 5, h.y - 5, 10, 10, 0);
        if (h.mouse > 0) this.sprite(MOUSE, h.x - 3, h.y - 3, 3);
      }
    } else if (r instanceof BookshelfRoom) {
      for (const s of r.shelves) this.box(s.x, s.y, s.w, s.h, 2);
      this.outline(r.table.x, r.table.y, r.table.w, r.table.h, 3);
      for (const f of r.ferns) if (f.state !== 'down') this.box(f.x, f.y, f.w, f.h, 1);
      const s = r.spider;
      this.sprite(SPIDER, s.x, s.y, 2);
      this.box(s.x + s.w / 2, s.y + s.h, 1, WORLD_H - s.y - s.h, 3);
    }

    if (r instanceof BirdcageRoom) {
      this.outline(r.table.x, r.table.y, r.table.w, r.table.h, 3);
      const c = r.cage;
      if (c.state === 'broken') {
        this.outline(c.x, c.y, c.w, 8, 1);
      } else {
        this.outline(c.x, c.y, c.w, c.h, 1);
        for (let x = c.x + 4; x < c.x + c.w - 2; x += 5) this.box(x, c.y + 1, 1, c.h - 2, 1);
      }
      if (r.bird && !r.birdCaught) this.sprite(BIRD, r.bird.x, r.bird.y, 3, r.bird.vx < 0);
      const d = r.dog;
      this.sprite(DOG, d.x, d.y, 2, d.dir < 0);
    } else if (r instanceof DogsRoom) {
      for (const b of r.bowls) {
        this.outline(b.x, b.y, b.w, b.h, 1);
        if (b.level > 0) this.box(b.x + 2, b.y + 2, Math.round((b.w - 4) * b.level), b.h - 3, 3);
      }
      for (const d of r.dogs) {
        const lying = d.state === 'asleep' || d.state === 'growl';
        this.sprite(lying ? DOG_LYING : DOG, d.x, d.y, 2, d.dir < 0);
        if (d.state === 'growl' && this.frame % 10 < 5) this.box(d.x + d.w / 2 - 1, d.y + d.h + 2, 3, 3, 3);
        if (d.meter > 0.02 && (d.state === 'asleep' || d.state === 'growl')) {
          this.outline(d.x + 3, d.y + d.h + 6, 18, 4, 3);
          this.box(d.x + 4, d.y + d.h + 7, Math.round(16 * d.meter), 2, d.state === 'growl' ? 2 : 1);
        }
        if (d.state === 'asleep' && this.frame % 40 < 20) this.box(d.x + d.w - 2, d.y + d.h + 3, 2, 2, 3);
      }
      const c = r.carton;
      this.outline(c.x, c.y, c.w, c.h, 3);
    } else if (r instanceof FishbowlRoom) {
      this.outline(r.table.x, r.table.y, r.table.w, r.table.h, 3);
      const b = r.bowl;
      this.outline(b.x, b.y, b.w, b.h, 1);
      this.box(b.x + 2, b.y + 2, b.w - 4, b.h * 0.7, 1);
    }

    const b = r.broom;
    this.box(b.x + 3, b.y + 8, 2, b.h - 8, 3);
    this.box(b.x, b.y, b.w, 8, 1);
  }

  private drawTank(r: FishbowlRoom): void {
    for (let y = 0; y < WATER_SURFACE; y += 4) this.box(0, y, WORLD_W, 1, 1);
    this.box(0, WATER_SURFACE, WORLD_W, 1, 3);
    for (const f of r.fish) this.sprite(FISH, f.x, f.y, 3, f.vx < 0);
    for (const e of r.eels) {
      this.box(e.x, e.y, e.w, e.h, 2);
      if (this.frame % 6 < 3) this.box(e.x + e.w / 2 - 1, e.y + e.h, 2, 2, 3);
    }
  }

  setVisible(on: boolean): void {
    this.canvas.hidden = !on;
  }
}
